/**
 * Admin Users API Client
 * Typed API client for admin user management endpoints
 * All endpoints are admin-only and require authentication
 */

import axiosInstance from '../../../../lib/axios';
import type {
  User,
  UserStatsData,
  UserUsageAnalytics,
  UserMediaUsage,
  AdvanceTokenLedgerHistory,
  AdminUserCredentials,
  GetUsersParams,
  GetUsersResponse,
  CreateUserInput,
  UpdateUserInput,
  UpdateUserStatusInput,
  AdjustUserTokenInput,
} from '../types';

// ============================================
// API Endpoints
// ============================================

const BASE_URL = '/v1/admin/users';

// ============================================
// User Stats
// ============================================

/**
 * Fetch user statistics
 * Backend calculates all stats - frontend does NOT compute
 * @returns UserStatsData
 */
export const getUserStats = async (): Promise<UserStatsData> => {
  const response = await axiosInstance.get<{ data: UserStatsData }>(`${BASE_URL}/stats`);
  return response.data.data;
};

export const getUserUsageAnalytics = async (publicId: string): Promise<UserUsageAnalytics> => {
  const response = await axiosInstance.get<{ data: UserUsageAnalytics }>(
    `${BASE_URL}/${publicId}/usage`
  );
  return response.data.data;
};

export const getUserMediaUsage = async (publicId: string): Promise<UserMediaUsage> => {
  const response = await axiosInstance.get<{ data: UserMediaUsage }>(
    `${BASE_URL}/${publicId}/media-usage`
  );
  return response.data.data;
};

export const getUserAdvanceTokenLedger = async (
  publicId: string,
  direction: 'credit' | 'debit',
  page = 1,
  pageSize = 20
): Promise<AdvanceTokenLedgerHistory> => {
  const response = await axiosInstance.get<{ data: AdvanceTokenLedgerHistory }>(
    `${BASE_URL}/${publicId}/advance-token-ledger`,
    { params: { direction, page, pageSize } }
  );
  return response.data.data;
};

export const getUserCredentials = async (publicId: string): Promise<AdminUserCredentials> => {
  const response = await axiosInstance.get<{ data: AdminUserCredentials }>(
    `${BASE_URL}/${publicId}/credentials`
  );
  return response.data.data;
};

export const revokeUserApiKey = async (
  publicId: string,
  credentialPublicId: string
): Promise<AdminUserCredentials> => {
  const response = await axiosInstance.patch<{ data: AdminUserCredentials }>(
    `${BASE_URL}/${publicId}/credentials/api-keys/${credentialPublicId}/revoke`
  );
  return response.data.data;
};

export const updateUserEmbedWidgetStatus = async (
  publicId: string,
  widgetPublicId: string,
  status: 'active' | 'disabled' | 'revoked'
): Promise<AdminUserCredentials> => {
  const response = await axiosInstance.patch<{ data: AdminUserCredentials }>(
    `${BASE_URL}/${publicId}/credentials/embed-widgets/${widgetPublicId}/status`,
    { status }
  );
  return response.data.data;
};

// ============================================
// User List
// ============================================

/**
 * Fetch paginated user list with optional filters
 * Supports search (username, email), role, status, plan, anonymous filters, and usage sorting
 * @param params - Query parameters for filtering and pagination
 * @returns Paginated user list
 */
export const getUsers = async (params?: GetUsersParams): Promise<GetUsersResponse> => {
  const response = await axiosInstance.get<{ data: GetUsersResponse }>(BASE_URL, {
    params: {
      search: params?.search || undefined,
      role: params?.role || undefined,
      status: params?.status || undefined,
      plan: params?.plan || undefined,
      anonymous: params?.anonymous || undefined,
      registrationProvider: params?.registrationProvider || undefined,
      registrationClient: params?.registrationClient || undefined,
      sortBy: params?.sortBy || undefined,
      sortOrder: params?.sortBy ? params?.sortOrder || 'desc' : undefined,
      page: params?.page || 1,
      limit: params?.limit || 10,
    },
  });
  return response.data.data;
};

// ============================================
// Create User
// ============================================

/**
 * Create a new user (Admin only)
 * Backend handles:
 * - Sending invite email
 * - Generating initial password or magic link
 * - Email validation
 * @param data - User creation data
 * @returns Created user
 */
export const createUser = async (data: CreateUserInput): Promise<User> => {
  const response = await axiosInstance.post<{ data: User }>(BASE_URL, data);
  return response.data.data;
};

// ============================================
// Update User
// ============================================

/**
 * Update user role and plan
 * Email and username are read-only
 * @param publicId - User's public ID
 * @param data - Fields to update (role, plan)
 * @returns Updated user
 */
export const updateUser = async (publicId: string, data: UpdateUserInput): Promise<User> => {
  const response = await axiosInstance.patch<{ data: User }>(`${BASE_URL}/${publicId}`, data);
  return response.data.data;
};

// ============================================
// Update User Status (Activate/Suspend)
// ============================================

/**
 * Toggle user status (active/suspended)
 * This is a soft state change - no hard delete
 * Suspended users remain visible in the system
 * @param publicId - User's public ID
 * @param data - Status update data
 * @returns Updated user
 */
export const updateUserStatus = async (
  publicId: string,
  data: UpdateUserStatusInput
): Promise<User> => {
  const response = await axiosInstance.patch<{ data: User }>(
    `${BASE_URL}/${publicId}/status`,
    data
  );
  return response.data.data;
};

// ============================================
// Adjust User Advance Tokens
// ============================================

/**
 * Credit or debit a user's advance tokens.
 * Backend records every change in TokenLedgerEntry.
 */
export const adjustUserTokens = async (
  publicId: string,
  data: AdjustUserTokenInput
): Promise<User> => {
  const response = await axiosInstance.post<{ data: User }>(`${BASE_URL}/${publicId}/tokens`, data);
  return response.data.data;
};

// ============================================
// Delete User (Not Implemented)
// ============================================

/**
 * Hard delete is NOT supported
 * Use updateUserStatus to suspend users instead
 */
// export const deleteUser = async (publicId: string): Promise<void> => {
//   throw new Error('Hard delete is not supported. Use updateUserStatus to suspend users.');
// };
